import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Link2, Search } from "lucide-react";
import { FadeUp } from "@/components/motion";
import { CATEGORIES } from "@/data/products";
import { useSite } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const loginWithGoogle = () => {
  const redirectUrl = window.location.origin + "/specs";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const SpecLibrary = () => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    document.title = "Spec Sheet Library | SanCub";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    robots.id = "noindex-meta";
    document.head.appendChild(robots);
    return () => document.getElementById("noindex-meta")?.remove();
  }, []);

  useEffect(() => {
    if (user) return;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch (e) {
        setUser(null);
      } finally {
        setAuthChecked(true);
      }
    };
    check();
  }, [user]);

  const { products } = useSite();
  const items = useMemo(
    () =>
      products.filter((p) => !p.skuBase)
        .filter((p) => cat === "All" || p.category === cat)
        .filter((p) => {
          const q = query.trim().toLowerCase();
          return !q || p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q);
        }),
    [products, query, cat]
  );

  const copyLink = useCallback((p) => {
    const url = `${window.location.origin}/sheets/${p.id}.pdf`;
    navigator.clipboard.writeText(url).then(
      () => toast.success(`Link copied for ${p.sku || p.name}`),
      () => toast.error("Couldn't copy — long-press the button instead.")
    );
  }, []);

  const copyImage = useCallback(async (p) => {
    const src = p.techImage || Object.values(p.techImages || {})[0];
    if (!src) return;
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const bmp = await createImageBitmap(blob);
      const canvas = document.createElement("canvas");
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bmp, 0, 0);
      const png = await new Promise((r) => canvas.toBlob(r, "image/png"));
      await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
      toast.success("Image copied — paste it into your message or quote");
    } catch (e) {
      navigator.clipboard.writeText(`${window.location.origin}${p.techImage || Object.values(p.techImages || {})[0]}`).then(
        () => toast.success("Image link copied"),
        () => toast.error("Couldn't copy this image")
      );
    }
  }, []);

  if (!authChecked) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="spec-auth-loading">
        <p className="text-sm font-medium text-ink/50">Checking access…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6 pt-24" data-testid="spec-login">
        <FadeUp className="w-full max-w-md">
          <div className="rounded-[2rem] bg-white p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-14">
            <img src="/logo-black.webp" alt="SanCub" className="mx-auto h-10 w-auto" />
            <h1 className="mt-8 font-display text-3xl font-medium tracking-tight text-ink">Spec sheet library</h1>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">
              Internal tool — sign in with your authorised Google account to find and share spec sheets.
            </p>
            <button
              onClick={loginWithGoogle}
              data-testid="spec-google-login-btn"
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z" />
              </svg>
              Sign in with Google
            </button>
          </div>
        </FadeUp>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-32 md:px-10 md:pt-40" data-testid="spec-library-page">
      <FadeUp>
        <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50">Internal — team only</p>
      </FadeUp>
      <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <h1 className="font-display text-5xl font-medium leading-none tracking-tight text-ink md:text-6xl" data-testid="spec-library-headline">
          Spec sheets
        </h1>
        <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="spec-count">
          {items.length} sheets
        </p>
      </div>

      <FadeUp delay={0.1} className="mt-10 flex flex-col gap-4">
        <div className="relative" data-testid="spec-search-wrap">
          <Search className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or code — e.g. 81H78 or basin…"
            data-testid="spec-search-input"
            className="w-full rounded-full border border-ink/10 bg-white py-4 pl-12 pr-6 text-sm text-ink placeholder:text-ink/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] outline-none transition-colors duration-300 focus:border-ink/40"
          />
        </div>
        <div className="no-scrollbar -mx-6 flex flex-nowrap gap-2 overflow-x-auto px-6 pb-1 md:mx-0 md:flex-wrap md:px-0" data-testid="spec-category-filters">
          {CATEGORIES.filter((c) => c !== "Vanities").map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`spec-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`shrink-0 rounded-full px-4 py-2 text-[12px] font-semibold transition duration-300 ${
                cat === c ? "bg-ink text-white" : "bg-white text-ink/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:text-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </FadeUp>

      <div className="mt-8 space-y-3" data-testid="spec-list">
        {items.map((p) => (
          <FadeUp key={p.id} y={10}>
            <div
              className="flex items-center gap-4 rounded-3xl bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:gap-6 md:p-5"
              data-testid={`spec-row-${p.id}`}
            >
              {p.techImage || p.techImages ? (
                <button
                  type="button"
                  onClick={() => copyImage(p)}
                  data-testid={`spec-image-${p.id}`}
                  title="Click to copy dimension drawing"
                  className="flex h-14 w-14 shrink-0 cursor-copy items-center justify-center rounded-2xl bg-sand transition duration-300 hover:ring-2 hover:ring-ink/30"
                >
                  <img src={p.techImage || Object.values(p.techImages || {})[0]} alt="Dimension drawing" className="max-h-full w-auto max-w-full object-contain p-1" loading="lazy" />
                </button>
              ) : (
                <span
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-dashed border-ink/15 bg-sand/50"
                  data-testid={`spec-coming-soon-${p.id}`}
                >
                  <span className="px-1 text-center text-[8px] font-bold uppercase leading-tight tracking-wide text-ink/35">
                    Coming soon
                  </span>
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-medium tracking-tight text-ink md:text-lg">{p.name}</p>
                <p className="mt-0.5 text-[12px] font-medium text-ink/40">
                  {p.sku ? `${p.sku} · ` : ""}{p.category}{p.techImage || p.techImages ? " · includes dimensions" : ""}
                </p>
              </div>
              <button
                onClick={() => copyLink(p)}
                data-testid={`spec-copy-${p.id}`}
                className="flex shrink-0 items-center gap-2 rounded-full border border-ink/10 px-4 py-2.5 text-[12px] font-semibold text-ink transition duration-300 hover:bg-sand"
              >
                <Link2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Copy link</span>
              </button>
              <a
                href={`/sheets/${p.id}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`spec-open-${p.id}`}
                className="flex shrink-0 items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-[12px] font-semibold text-white transition duration-300 hover:bg-ink/90"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Open PDF</span>
              </a>
            </div>
          </FadeUp>
        ))}
        {items.length === 0 && (
          <p className="rounded-3xl bg-white py-16 text-center text-sm text-ink/50" data-testid="spec-empty">
            No sheets match that search.
          </p>
        )}
      </div>
    </main>
  );
};

export default SpecLibrary;
